/**
 * The Request Detail edit session for deliverables.
 *
 * Deliverables behave like every other request field: the section is read-only until Edit
 * is pressed, changes are held here as pending state, Save commits them and Cancel throws
 * them away. Nothing writes through.
 *
 * It follows the shape the repeatable sections already set — a **save handle** with
 * `hasPending…` / `flushPending…`, which `RequestDetail` gates its "no changes to save"
 * check on and flushes alongside the rack and UPS sections. It is deliberately *only* that
 * shape: no draft service, no offline sync, no query keys, no engine bridge. Those belong
 * to phase 2 against a real table (see the spec's "Why not the real repeatable-section
 * pattern").
 *
 * Lives in `RequestDetail` rather than in the step, because the section is unmounted
 * whenever the user is on another step and the pending changes have to outlive that.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  applyDeliverableComment,
  applyDeliverableEntries,
  commitDeliverables,
  deliverableListsMatch,
  listDeliverables,
  readDeliverables,
  withoutDeliverableType,
} from '../services/deliverablesService';
import { networkService } from '../services/network';

import type {
  RequestDeliverable,
  RequestDeliverableEntry,
  RequestDeliverableType,
} from '../types/requestDeliverables';

export const DELIVERABLES_LOAD_ERROR_MESSAGE =
  'Could not load deliverables. Reload the page to try again.';

/**
 * Shown when the device is offline and has never cached this request, so it cannot know what
 * the request's deliverables are. Deliberately a notice and not an `error`: nothing failed,
 * and calling it an error would send the user hunting for a fault.
 *
 * No longer covers "there genuinely are none" — `readDeliverables` now reports that as a
 * loaded empty list, and only a genuinely absent local copy reaches here. The section is
 * read-only in this state, because a save from an unknown base would delete the real rows.
 */
export const DELIVERABLES_OFFLINE_EMPTY_MESSAGE =
  'You are offline and this request has no locally stored deliverables. Reconnect to see them.';

/**
 * Shown when a save reached neither the server nor the offline queue — storage full, or an
 * offline-created request that synced out from under the save. The pending list is kept, so
 * retrying after the cause clears will still send what the user typed.
 */
export const DELIVERABLES_SAVE_FAILED_MESSAGE =
  'Could not save deliverables. Your changes are still here — try saving again.';

/**
 * Shown when the server refused the save because it would have deleted deliverables this
 * device never saw — someone else changed the request while this list was open. Distinct
 * from the failure message on purpose: retrying cannot help, because the base the retry
 * would send is the one the server has already rejected.
 *
 * Says "reopen", not "reload the page": this screen runs on iOS and Android as well as web,
 * where there is no page to reload. (MR !489 review, batch 1b.)
 */
export const DELIVERABLES_SAVE_CONFLICT_MESSAGE =
  "This request's deliverables changed while you were editing, so your changes were not saved. Reopen the request to see the current list, then make your change again.";

/**
 * What a flush actually achieved, so the caller can report it honestly.
 *
 * It used to return `void`, which meant a caller could not tell a committed write from one
 * the server refused: the outcome was recorded on the section's own `error` state and every
 * caller went on to toast "Changes saved successfully!". That is the same
 * report-success-for-a-write-that-did-not-happen defect the rest of this batch exists to
 * close, so the outcome is now a value rather than a side effect.
 *
 * A refusal must NOT be raised as an exception: this flush is one step of a save that also
 * writes racks, UPS units and attachments, and aborting those over a deliverables conflict
 * would turn one refused section into a wholly failed save. (MR !489 review, batch 1b.)
 */
export type DeliverablesFlushOutcome =
  /** Reached the server. */
  | 'saved'
  /** No network, but durably queued and will replay. */
  | 'queued'
  /** Nothing to write, or the unsaved-request sentinel whose list lives in form state. */
  | 'nothing'
  /** The server refused: this device's base could not prove the deletions it asked for. */
  | 'conflict'
  /** Reached neither the server nor the queue. The pending list is kept. */
  | 'failed';

/** Mirrors the repeatable sections' save handles, narrowed to what this section needs. */
export interface DeliverablesSaveHandle {
  hasPendingDeliverableChanges(): boolean;
  flushPendingDeliverableChanges(): Promise<DeliverablesFlushOutcome>;
}

export interface DeliverablesEditSession {
  /** What the section and the dialog render: the pending list if there is one, else stored. */
  deliverables: RequestDeliverable[];
  /**
   * Whether there is something a save would write. The submit dialog's "Save changes" control
   * is gated on it.
   *
   * Derived from **state**, while `saveHandle.hasPendingDeliverableChanges()` answers the same
   * question from the refs. Async save paths must keep using the handle, whose refs cannot go
   * stale mid-flush.
   *
   * **Not** because refs would render late — every ref write in this hook is paired with a
   * `setState`, so a ref read during render is in fact fresh. That was the original claim here
   * and a mutation test disproved it (2026-08-12): swapping this to the refs kept all 83 tests
   * green. It is state-derived because that pairing is an implicit invariant of
   * `setPendingList` / `setPersistedList` rather than a guaranteed one, and a future ref-only
   * write would silently freeze a control gated on it. Deriving from state cannot drift.
   */
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  /**
   * False when the read could not establish a trustworthy starting list — offline with no
   * local copy, a failed load, or a stale `o-*` id.
   *
   * Callers MUST render the editor read-only when this is false. A save sends the whole list
   * and the server replaces what it holds, so editing from a base that is empty only because
   * the device never saw the real one deletes every row on the server.
   * (MR !489 review, finding 01.)
   */
  isBaseTrusted: boolean;
  error: string | null;
  /** Non-error explanation of what is on screen — currently only the offline-empty case. */
  notice: string | null;
  addDeliverableEntries(entries: RequestDeliverableEntry[]): void;
  editDeliverableComment(
    itemType: RequestDeliverableType,
    comment: string,
  ): void;
  removeDeliverableType(itemType: RequestDeliverableType): void;
  /** Cancel — drops every pending change and falls back to what is stored. */
  discardPendingDeliverables(): void;
  /**
   * Takes a restore point and hands back the undo. The submit dialog uses it so that
   * cancelling the submit discards what was typed *in the dialog* without also discarding
   * whatever the surrounding edit session had pending.
   */
  snapshotPendingDeliverables(): () => void;
  saveHandle: DeliverablesSaveHandle;
}

export function useDeliverablesEditSession(
  requestId: string | undefined,
): DeliverablesEditSession {
  const [persisted, setPersisted] = useState<RequestDeliverable[]>([]);
  const [pending, setPending] = useState<RequestDeliverable[] | null>(null);
  // Starts true, not false. With `false` there was a first render where the list was empty
  // and nothing said it had not loaded yet, which is what let the submit dialog open onto an
  // empty checklist for a request that had deliverables. (MR !489 review, finding 01.)
  const [isLoading, setIsLoading] = useState(true);
  const [isBaseTrusted, setIsBaseTrusted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The save handle is read from async save paths, where a state closure captured on the
  // last render is a real hazard. Refs are written in the same call as the setState so the
  // handle always sees what the user last did, not what the last render saw.
  const persistedRef = useRef<RequestDeliverable[]>([]);
  const pendingRef = useRef<RequestDeliverable[] | null>(null);
  /**
   * What this device can prove the **server** holds, which is what a save must send as its
   * base. Tracked apart from `persisted` because the two diverge exactly where it matters:
   * after a queued offline edit `persisted` is the user's own unsent list, and offering that
   * as proof would ask the server to authorise the user's removal on the strength of the
   * removal itself. Ref-only, because nothing renders it. (MR !489 review, batch 1b.)
   */
  const serverBaseRef = useRef<RequestDeliverable[]>([]);

  const setPersistedList = useCallback((next: RequestDeliverable[]) => {
    persistedRef.current = next;
    setPersisted(next);
  }, []);

  const setPendingList = useCallback((next: RequestDeliverable[] | null) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  // Connectivity is a load input, not just a save input: a section opened offline shows an
  // empty list with a notice, and it has to re-read itself when the network comes back
  // rather than sit on that notice until the user navigates away. Local state, not
  // `useNetworkStatus` — that hook also kicks off a `fullSync`, which is not this hook's job.
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    networkService.isConnected(),
  );

  useEffect(() => networkService.addListener(setIsOnline), []);

  // Pending edits belong to the request, not to the connection. Reset them only when the
  // request changes, so a reconnect mid-edit re-reads without discarding what was typed.
  //
  // The base is reset here for the same reason and on the same key. It must NOT be reset in
  // the load effect below, which also re-runs on every connectivity flip: a save landing in
  // that window would send an empty base and be refused for a removal that was never stale,
  // telling the user to reopen a request nobody else had touched.
  useEffect(() => {
    setPendingList(null);
    serverBaseRef.current = [];
  }, [requestId, setPendingList]);

  useEffect(() => {
    if (!requestId) {
      setPersistedList([]);
      serverBaseRef.current = [];
      setError(null);
      setNotice(null);
      setIsBaseTrusted(false);
      setIsLoading(false);
      return;
    }

    let active = true;
    setIsLoading(true);
    (async () => {
      try {
        // `readDeliverables`, not `listDeliverables`: offline it answers from the request's
        // own offline queue or its cached copy rather than going to the wire and throwing.
        const result = await readDeliverables(requestId);
        if (!active) return;

        if (result.status === 'unavailable') {
          // No trustworthy base. Show whatever was cached so the screen is not blank, but
          // the section must not be editable: a save from here would send a list the device
          // cannot vouch for and the server would delete the difference.
          setPersistedList(result.cached);
          // Nothing was proved, so nothing may be deleted. The editor is read-only in this
          // state anyway; this is the second line of defence rather than the first.
          serverBaseRef.current = [];
          setIsBaseTrusted(false);
          setError(
            result.reason === 'load-failed'
              ? DELIVERABLES_LOAD_ERROR_MESSAGE
              : null,
          );
          setNotice(
            result.reason === 'offline-no-cache'
              ? DELIVERABLES_OFFLINE_EMPTY_MESSAGE
              : null,
          );
          return;
        }

        setPersistedList(result.deliverables);
        serverBaseRef.current = result.base;
        setIsBaseTrusted(true);
        setError(null);
        // Only when there is nothing to show. A cached list answered offline is a real
        // answer, and telling the user to reconnect to see it would be wrong.
        setNotice(
          result.offline && result.deliverables.length === 0
            ? DELIVERABLES_OFFLINE_EMPTY_MESSAGE
            : null,
        );
      } catch (loadError) {
        // Guarded rather than left to escape: an unhandled rejection here would show the
        // user an empty list that looks like "no deliverables yet".
        console.error('Failed to load request deliverables:', loadError);
        if (active) {
          setError(DELIVERABLES_LOAD_ERROR_MESSAGE);
          setNotice(null);
          serverBaseRef.current = [];
          setIsBaseTrusted(false);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [requestId, isOnline, setPersistedList]);

  const addDeliverableEntries = useCallback(
    (entries: RequestDeliverableEntry[]) => {
      if (entries.length === 0) return;
      const base = pendingRef.current ?? persistedRef.current;
      setPendingList(applyDeliverableEntries(base, requestId ?? '', entries));
    },
    [requestId, setPendingList],
  );

  const editDeliverableComment = useCallback(
    (itemType: RequestDeliverableType, comment: string) => {
      const base = pendingRef.current ?? persistedRef.current;
      setPendingList(applyDeliverableComment(base, itemType, comment));
    },
    [setPendingList],
  );

  const removeDeliverableType = useCallback(
    (itemType: RequestDeliverableType) => {
      const base = pendingRef.current ?? persistedRef.current;
      setPendingList(withoutDeliverableType(base, itemType));
    },
    [setPendingList],
  );

  const discardPendingDeliverables = useCallback(() => {
    setPendingList(null);
  }, [setPendingList]);

  const snapshotPendingDeliverables = useCallback(() => {
    const restorePoint = pendingRef.current;
    return () => setPendingList(restorePoint);
  }, [setPendingList]);

  const saveHandle = useMemo<DeliverablesSaveHandle>(
    () => ({
      // A pending list that ended up matching what is stored is not a change: adding a
      // deliverable and removing it again must still report "No changes to save".
      hasPendingDeliverableChanges: () =>
        pendingRef.current !== null &&
        !deliverableListsMatch(pendingRef.current, persistedRef.current),
      flushPendingDeliverableChanges:
        async (): Promise<DeliverablesFlushOutcome> => {
          const next = pendingRef.current;
          if (!requestId || next === null) return 'nothing';

          // The proof of what the server holds, NOT `persistedRef.current`. They are the same
          // value on every path except a queued read, and that one exception is the whole
          // point: sending the queued list as the base would have the server authorise the
          // user's own offline removal on the strength of that removal.
          const outcome = await commitDeliverables(
            requestId,
            serverBaseRef.current,
            next,
          );

          if (outcome === 'conflict') {
            // The server refused: the list moved under us. Keep `pending` — it is the user's
            // only copy — and say so in terms that point at reopening rather than retrying.
            setError(DELIVERABLES_SAVE_CONFLICT_MESSAGE);
            // The base has been positively disproved, so it must stop being treated as one.
            // Without this the editor stays open on a list built from a base the server has
            // already rejected, and every further Save re-sends that same base for the same
            // refusal. Marking it untrusted makes the section read-only, which is what turns
            // the message into something the user can act on. (MR !489 review, batch 1b.)
            serverBaseRef.current = [];
            setIsBaseTrusted(false);
            return 'conflict';
          }

          if (outcome === 'failed') {
            // Nothing reached the server or the queue. Keep `pending` — it is now the only
            // copy of what the user typed — and surface it rather than reporting a save.
            // Promoting here was how a storage-quota failure silently ate an edit.
            // (MR !489 review, finding 01.)
            setError(DELIVERABLES_SAVE_FAILED_MESSAGE);
            return 'failed';
          }

          if (outcome === 'skipped') {
            // Nothing was written anywhere — this is the unsaved-request sentinel, whose
            // deliverables live in Create Request's form state. Clearing `pending` here
            // would throw the user's edit away, and promoting it to `persisted` would claim
            // a write that never happened.
            return 'nothing';
          }

          if (outcome === 'sent') {
            // Only re-read when the write actually reached the server. Queued, this second
            // call would go to the wire and throw, undoing an otherwise successful save.
            //
            // Guarded, because the write has ALREADY landed by this point — `commitDeliverables`
            // returned 'sent' and updated the cache. Letting a failed refresh escape reported a
            // successful save as a failure: the caller toasted "could not save", `pending` was
            // kept, and the user was invited to retry a write that had committed. The list we
            // just sent is the correct fallback; the next load reconciles anything else.
            try {
              const refreshed = await listDeliverables(requestId);
              setPersistedList(refreshed);
              serverBaseRef.current = refreshed;
            } catch (refreshError) {
              console.error(
                'Deliverables saved, but the re-read failed:',
                refreshError,
              );
              setPersistedList(next);
              // The write committed, so the server holds `next` whether or not the re-read
              // came back. Advancing the base is what lets a second save in the same session
              // remove something the first one added.
              serverBaseRef.current = next;
            }
          } else {
            // Queued: what the user typed is now the stored truth as far as this device is
            // concerned, so the "nothing stored locally" notice is no longer true either.
            setPersistedList(next);
            setNotice(null);
            // The base deliberately does NOT move. Nothing reached the server, so what this
            // device can prove about the server is exactly what it could prove before, and
            // the queue is holding that same value for the replay.
          }

          setPendingList(null);
          return outcome === 'sent' ? 'saved' : 'queued';
        },
    }),
    [requestId, setPendingList, setPersistedList],
  );

  return {
    deliverables: pending ?? persisted,
    // Same rule as the save handle's: a pending list that ended up matching what is stored is
    // not a change, so adding a deliverable and removing it again reports nothing to save.
    hasUnsavedChanges:
      pending !== null && !deliverableListsMatch(pending, persisted),
    isLoading,
    isBaseTrusted,
    error,
    notice,
    addDeliverableEntries,
    editDeliverableComment,
    removeDeliverableType,
    discardPendingDeliverables,
    snapshotPendingDeliverables,
    saveHandle,
  };
}
